// src/features/agent-builder/components/DeploymentInstructions.tsx
import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Button, Tooltip } from '@dopomogai/ui';
import { renderIcon } from '@dopomogai/utils';
import clsx from 'clsx';

interface DeploymentInstructionsProps {
  embedScriptContent: string; // The full <script>...</script> tag as a string
  helpArticleUrl?: string; // URL for the installation guide
}

export const DeploymentInstructions: React.FC<DeploymentInstructionsProps> = ({
  embedScriptContent,
  helpArticleUrl,
}) => {
  const { t } = useTranslation('agentBuilder');
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(embedScriptContent).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    }).catch(err => {
      console.error('Failed to copy text: ', err);
      // TODO: Add user feedback for copy failure (e.g., a toast)
    });
  };

  const mountPointExample = `<div id="your-widget-mount-point"></div>`; // Example

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-md font-semibold text-gray-700 mb-1">
          {/* i18n: agentBuilder.step5.deploy.step1Title = "1. Add a Mount Point" */}
          {t('step5.deploy.step1Title')}
        </h4>
        <p className="text-sm text-gray-600 mb-2">
          {/* i18n: agentBuilder.step5.deploy.mountPointInstruction = "Place this div where you want the chat widget to appear on your page:" */}
          {t('step5.deploy.mountPointInstruction')}
        </p>
        <div className="relative bg-slate-800 text-slate-200 p-4 rounded-md font-mono text-xs">
          <pre className="overflow-x-auto whitespace-pre-wrap"><code>{mountPointExample}</code></pre>
          {/* A copy button for this part is less common but can be added if desired */}
        </div>
         <p className="mt-1 text-xs text-slate-500">
            {/* i18n: agentBuilder.step5.deploy.mountPointNote = "Ensure the <1>id</1> attribute here matches the <1>data-mount-id</1> in the script below (e.g., if you change it from 'your-widget-mount-point')." */}
            <span dangerouslySetInnerHTML={{ __html: t('step5.deploy.mountPointNote')}} />
        </p>
      </div>

      <div>
        <h4 className="text-md font-semibold text-gray-700 mb-1">
          {/* i18n: agentBuilder.step5.deploy.step2Title = "2. Add the Script" */}
          {t('step5.deploy.step2Title')}
        </h4>
        <p className="text-sm text-gray-600 mb-2">
          {/* i18n: agentBuilder.step5.deploy.embedInstruction = "Copy this code and paste it just before the closing </body> tag on your website:" */}
          {t('step5.deploy.embedInstruction')}
        </p>
        <div className="relative bg-slate-800 text-slate-200 p-4 rounded-md font-mono text-xs group">
          <pre className="overflow-x-auto whitespace-pre-wrap">
            <code>{embedScriptContent}</code>
          </pre>
          <Tooltip text={copied ? t('common.copied') : t('common.copyToClipboard')}>
            <button
              onClick={handleCopy}
              aria-label={copied ? t('common.copied') : t('common.copyToClipboard')}
              className={clsx(
                "absolute top-2 right-2 p-1.5 rounded-md transition-all duration-150 ease-in-out",
                "bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-slate-100",
                "opacity-50 group-hover:opacity-100 focus:opacity-100 focus:ring-2 focus:ring-sky-400 focus:outline-none"
              )}
            >
              {copied ? renderIcon('check', { size: 16 }) : renderIcon('copy', { size: 16 })}
            </button>
          </Tooltip>
        </div>
      </div>

      {helpArticleUrl && (
        <div className="pt-2 text-sm">
          {/* i18n: agentBuilder.step5.deploy.helpLinkText = "Need help with installation? <1>Read our guide</1>." */}
          <Trans i18nKey="step5.deploy.helpLinkText" ns="agentBuilder">
            Need help with installation? 
            <a
              href={helpArticleUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-indigo-600 hover:text-indigo-700 hover:underline font-medium"
            >
              Read our guide
              {renderIcon('externallink', { size: 14, className: "ml-1" })}
            </a>
            .
          </Trans>
        </div>
      )}
    </div>
  );
};